/**
 * O formulário de Negócio (FR-3 emendado em 21/10/2026, FR-13) — o Negócio nasce numa Empresa
 * **escolhida**.
 *
 * **Até a 13.1 todo envio criava uma Empresa nova** (`POST /empresas/enxuta` antes do
 * `POST /negocios`), e foi assim que nasceu a segunda Veluplast e que a Regiane não achou a
 * O. V. D. (chamado de 21/10, P3, P4, P10). Agora o campo Empresa **sugere** as cadastradas
 * (`CampoDeEmpresa`, sobre `GET /empresas?busca=`, sem diferença de acento e pontuação), e a última
 * opção, "Criar nova empresa", abre a ficha completa do "+ Empresa" **num modal**: salva a ficha, a
 * Empresa fica escolhida. O formulário **nunca cria Empresa sozinho**, e não pede meio de contato:
 * o contato é da ficha (decisão do Gustavo, 21/10). Envio é **só** `POST /negocios`. A rota enxuta
 * continua no serviço, sem chamador aqui.
 *
 * **O modal da ficha mora fora do `<form>` do Negócio**, como irmão dele: o `submit` do React sobe
 * pela árvore de componentes (inclusive através de portal), e uma ficha filha deste `<form>`
 * dispararia o `enviar()` do Negócio. O Responsável da Empresa criada ali começa no Responsável
 * escolhido no Negócio, editável — é o que a porta enxuta fazia.
 *
 * **Vindo do WhatsApp** (o "+ Negócio para esta pessoa"), a janela recebe os ids da conversa:
 * `empresaInicialId` abre com aquela Empresa escolhida (fecha a DW-360); `pessoaSoltaId` relê a
 * Pessoa (`GET /pessoas?ids=`) e, se ela continua solta, a ficha nova traz o nome e o telefone do
 * contato, e **depois de o Negócio nascer** a mesma Pessoa é vinculada à Empresa dele
 * (`POST /pessoas/{id}/empresa`), com aviso. O vínculo vem depois, e não na escolha: escolher e
 * desistir não pode deixar o contato pendurado numa ficha. É composição do SPA (AD-25), não
 * atômica; a falha só do vínculo é recuperável ("Tentar de novo") sem recriar o Negócio. Desde a
 * 13.3 o vínculo vai com `exigir_solta: true`: a rota deixa de **mover** a Pessoa que outra aba
 * vinculou entre a releitura e o `POST`, e o `409` dela vai direto ao aviso "de outra empresa".
 *
 * **Desde a 13.19c2 as perguntas vão às Empresas visíveis da Pessoa** (`empresa_ids`, a Pessoa em
 * várias Empresas): a Empresa só vem preenchida quando há **exatamente uma** em que quem cria pode
 * criar Negócio (as editáveis; code review, C — a vista só pela PA-3 não entra); "solta" é a lista
 * vazia (sem o campo, "não sei", e nada é vinculado); e "de outra" é a Empresa do Negócio fora dela
 * — escolhida uma das Empresas da Pessoa, o contato já está lá, e nada é vinculado.
 *
 * **Desde a 13.14b o vínculo é parte do "pronto"** (DW-383, a resposta do Gustavo: "o contato passa
 * a ser ligado à Empresa obrigatoriamente quando o Negócio nasce da conversa"). Com a falha, o Negócio
 * não aparece como criado sozinho: o alerta diz que o contato está solto (quem for Responsável não o
 * vê), "Criar negócio" fica travado até ligar, e a janela pergunta antes de fechar. A releitura em
 * erro ao abrir não dispensa o vínculo — ele é tentado depois do Negócio. O `404` (e a Pessoa que
 * some do lote) é "fora do alcance": não há o que tentar. A janela sabe do estado por
 * `aoMudarOVinculo`, e trava o fechar enquanto o envio e o vínculo viajam (DW-372).
 *
 * **A ficha do modal mora em `modulos/cadastro/ModalDaNovaEmpresa.tsx`** desde a 13.3 — o painel do
 * contato do WhatsApp abre a mesma —, e daqui saem só os dois textos do "Seguir sem elas".
 *
 * **Nenhum campo de dinheiro.** A coluna `valor` do Negócio existe e nasce nula, mas ela não está no
 * contrato da rota (FR-62). **Frete e Negociação saíram na 13.16** (itens J e K do teste de
 * 26/10/2026): da tela e do contrato — as colunas ficam no banco com o que tinham.
 *
 * **Validação local só do que o cliente já sabe** — Empresa escolhida, Responsável e Etapa; tudo o
 * mais que o servidor recusar aparece verbatim. **Paridade de celular (NFR-1):** os mesmos campos, a
 * caixa de sugestão e o modal da ficha existem nas duas larguras; a grade cai para uma coluna por
 * `useEhCelular()`.
 */

import { useQuery } from '@tanstack/react-query'
import { type FormEvent, useEffect, useRef, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import {
  CampoDeEmpresa,
  type EmpresaEscolhida,
} from '@/modulos/cadastro/CampoDeEmpresa'
import {
  ModalDaNovaEmpresa,
  PERGUNTA_DA_NOVA_EMPRESA,
  telefoneDaConversaNaFicha,
} from '@/modulos/cadastro/ModalDaNovaEmpresa'
import {
  CONTATO_DO_WHATSAPP_VINCULADO,
  type Pessoa,
  ehSolta,
  empresaIdsEditaveisDe,
  estaNaEmpresa,
  lerPessoasPorIds,
  useEmpresa,
  useVincularPessoaAEmpresa,
} from '@/modulos/cadastro/api'
import { nomeExibidoDaEmpresa } from '@/modulos/cadastro/nome-da-empresa'
import { useResponsaveis } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { LinhaDoRotulo } from '@/shell/MarcaDeObrigatorio'
import { useEhCelular } from '@/shell/Shell'
import { type Funil, useCriarNegocio, useFunis, useNegociosDaPessoa } from './api'
import './formulario-de-negocio.css'

const TITULO = 'Novo negócio'

const AJUDA =
  'Escolha a Empresa entre as cadastradas ou crie uma nova pela ficha completa. A sugestão não ' +
  'impede cadastrar outra parecida: confira a lista antes de criar.'

/**
 * As recusas locais. Exportadas porque a suíte as afirma pelo **nome**, e não pelo texto
 * redigitado. `FALTA_EMPRESA` é o texto da decisão P-A de 21/10/2026, e aparece **no campo**.
 */
export const FALTA_EMPRESA = 'Escolha uma Empresa da lista ou crie uma nova.'
export const FALTA_RESPONSAVEL = 'Escolha o Responsável pelo Negócio.'

/**
 * A recusa da Etapa: um Funil **sem Etapa nenhuma** é estado real (a 3.1 cria Funil vazio).
 */
export const FALTA_ETAPA =
  'Este Funil ainda não tem Etapas. Crie uma na Administração antes de seguir.'

/** O catálogo chegou **vazio**: não há Funil nenhum, e não é que o aberto esteja sem Etapa. */
export const NAO_HA_FUNIL =
  'Não há Funil cadastrado. Crie um na Administração antes de seguir.'

/** Os avisos da janela vinda do WhatsApp (13.1), com os textos da spec. */
export const EMPRESA_DA_CONVERSA_ILEGIVEL =
  'Não foi possível carregar a Empresa da conversa. Escolha-a na lista.'
export const CONTATO_DA_CONVERSA_ILEGIVEL = 'Não foi possível carregar o contato da conversa.'
/** O ERP achou a ficha do CNPJ digitado no modal, e a leitura dela falhou (13.1). */
export const EMPRESA_ENCONTRADA_ILEGIVEL =
  'A Empresa deste CNPJ já tem ficha, mas não foi possível carregá-la. Procure-a na lista.'
/** O envio enquanto a Empresa (da conversa, ou a que o ERP achou) ainda é lida (13.1). */
export const AINDA_CARREGANDO_A_EMPRESA =
  'A Empresa ainda está carregando. Aguarde um instante e envie de novo.'
export const CONTATO_VINCULADO = 'Contato do WhatsApp vinculado a esta empresa.'
export const CONTATO_DE_OUTRA_EMPRESA = 'Este contato já está vinculado a outra empresa.'
export const TENTAR_O_VINCULO_DE_NOVO = 'Tentar de novo'
/** 13.14b (DW-372): enquanto a Pessoa da conversa é ligada à Empresa do Negócio. */
export const CONTATO_SENDO_LIGADO = 'Ligando o contato do WhatsApp à Empresa do Negócio…'
/** 13.14b: a Pessoa sumiu do lote, ou o `404` do vínculo — não há o que tentar de novo. */
export const CONTATO_FORA_DO_ALCANCE =
  'Não foi possível ligar o contato: ele não está mais ao seu alcance.'

/** 13.14b (DW-383): o Negócio nasceu, e o contato ficou solto. */
export function contatoSolto(codigo: number): string {
  return (
    `O Negócio ${String(codigo)} foi criado, mas o contato do WhatsApp ainda não está ligado à ` +
    'Empresa dele: quem for Responsável não vê o contato. Tente de novo.'
  )
}

/** O que a janela precisa saber do vínculo (13.14b): no ar, solto (com o código) ou livre. */
export type EstadoDoVinculoNaJanela = 'livre' | 'no-ar' | 'pendente'

/**
 * O que o modal da ficha pergunta antes de descartar uma Empresa já preenchida. A ficha mora em
 * `modulos/cadastro/ModalDaNovaEmpresa.tsx` desde a 13.3; o texto continua exportado daqui.
 */
export { PERGUNTA_DA_NOVA_EMPRESA }

/** Enquanto os Funis viajam. Sem o aviso, "sem Funil" e "ainda não chegou" ficam iguais. */
const CARREGANDO_OS_FUNIS = 'Carregando os Funis…'

/** Falha ao ler o catálogo. Texto do SPA: é falha de carga, não mensagem de domínio. */
const FUNIS_INDISPONIVEIS =
  'Não foi possível carregar os Funis e as Etapas. Recarregue a página antes de seguir — sem ' +
  'eles não há onde o Negócio nascer.'

/**
 * Falha ao ler a equipe. O envio **continua saindo**: o Responsável cai em quem entrou, que a
 * sessão já traz.
 */
const EQUIPE_INDISPONIVEL =
  'Não foi possível carregar a equipe. O Responsável está em quem entrou: recarregue a página ' +
  'antes de enviar se o Negócio for de outra pessoa.'

/** Enquanto a Empresa da conversa (ou a recém-encontrada) é lida. */
const CARREGANDO_A_EMPRESA = 'Carregando a Empresa…'

/** O rótulo que ocupa a primeira linha de todo seletor opcional: "não escolhi nenhuma". */
const SEM_ESCOLHA = '— não informado —'

/** O verbo do botão, e o que ele diz enquanto a criação (e o vínculo) viajam. */
const CRIAR = 'Criar negócio'
const CRIANDO = 'Criando…'

/** O `409` do vínculo com `exigir_solta`: o contato já está noutra Empresa (13.3). */
const STATUS_DE_CONFLITO = 409
/** O `404` do vínculo: a Pessoa não está mais ao alcance de quem vincula (13.14b). */
const STATUS_DE_INEXISTENTE = 404

/** O bloco do contato solto — o "Criar negócio" travado aponta para ele (13.14b). */
const ID_DO_CONTATO_SOLTO = 'negocio-contato-solto'
const ID_DO_NEGOCIO_SEM_EMPRESA = 'negocio-ja-tem-negocio-sem-empresa'

/**
 * 13.20b2 (FR-3, recomendação do coordenador a confirmar): a Pessoa da conversa já tem um Negócio
 * em andamento que quem age vê — o sem Empresa, ou o que recebeu a Empresa e espera o contato
 * chegar a ela (code review, A1) — e a janela não cria outro, e leva a ele. Com o Negócio de outra
 * Responsável, o aviso diz de quem é, quando a tela tem o nome (B11).
 */
export function negocioDoContatoEmAndamento(semEmpresa: boolean, responsavel: string | null): string {
  const base = semEmpresa
    ? 'Esta pessoa já tem um negócio sem empresa em andamento'
    : 'Esta pessoa já tem um negócio em andamento'
  return responsavel === null ? `${base}.` : `${base}, com ${responsavel}.`
}
export const JA_TEM_NEGOCIO_SEM_EMPRESA = negocioDoContatoEmAndamento(true, null)
export const ABRIR_O_NEGOCIO = 'Abrir o negócio'
/** O recorte do contato que falhou: "não sei" — a janela não cria, e oferece a releitura (A2). */
export const TENTAR_O_RECORTE_DE_NOVO = 'Tentar de novo'

/** O código é o que a operação usa para se referir ao Negócio. */
function mensagemDeSucesso(codigo: number): string {
  return `Negócio criado com o código ${String(codigo)}.`
}

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** Qualquer letra — o nome do contato sem letra é o número, e não vira razão social. */
function temLetra(texto: string): boolean {
  return /\p{L}/u.test(texto)
}

/**
 * O estado do formulário: todo campo é texto, porque todo campo é um controle controlado. A
 * Empresa **não** está aqui: ela é uma escolha (um `id`), e não um texto.
 */
export interface Rascunho {
  etapa_id: string
  responsavel_id: string
  sdr_id: string
}

const RASCUNHO_VAZIO: Rascunho = {
  etapa_id: '',
  responsavel_id: '',
  sdr_id: '',
}

/** Campo opcional vazio vai como `null`, e não como `""`: "não informado" é um estado só. */
function ouNulo(valor: string): string | null {
  const aparado = valor.trim()
  return aparado === '' ? null : aparado
}

/** A recusa local, ou `null` quando não há nenhuma. O servidor continua sendo quem decide. */
export function recusaLocal(rascunho: Rascunho, empresaId: string | null): string | null {
  if (empresaId === null) {
    return FALTA_EMPRESA
  }
  if (rascunho.responsavel_id === '') {
    return FALTA_RESPONSAVEL
  }
  if (rascunho.etapa_id === '') {
    return FALTA_ETAPA
  }
  return null
}

function Campo({
  id,
  rotulo,
  largo = false,
  obrigatorio = false,
  children,
}: {
  id: string
  rotulo: string
  largo?: boolean
  /** O `*` vermelho logo depois do rótulo (13.15); o `aria-required` é de quem monta o controle. */
  obrigatorio?: boolean
  children: React.ReactNode
}) {
  return (
    <div className={largo ? 'negocio__campo negocio__campo--largo' : 'negocio__campo'}>
      <LinhaDoRotulo obrigatorio={obrigatorio}>
        <label className="negocio__rotulo" htmlFor={id}>
          {rotulo}
        </label>
      </LinhaDoRotulo>
      {children}
    </div>
  )
}

function Seletor({
  id,
  rotulo,
  valor,
  opcional = false,
  largo = false,
  aoTrocar,
  children,
}: {
  id: string
  rotulo: string
  valor: string
  opcional?: boolean
  largo?: boolean
  aoTrocar: (valor: string) => void
  children: React.ReactNode
}) {
  return (
    /* 13.15: o seletor que não é opcional é obrigatório — `*` no rótulo e `aria-required`. */
    <Campo id={id} rotulo={rotulo} largo={largo} obrigatorio={!opcional}>
      <select
        id={id}
        className="negocio__selecao"
        aria-required={opcional ? undefined : true}
        value={valor}
        onChange={(evento) => {
          aoTrocar(evento.target.value)
        }}
      >
        {/* Só o seletor opcional oferece "não informado". Nos obrigatórios a linha vazia
            existiria só para ser escolhida por engano — e a recusa local diria o óbvio. */}
        {opcional || valor === '' ? <option value="">{SEM_ESCOLHA}</option> : null}
        {children}
      </select>
    </Campo>
  )
}

/**
 * De onde vem a Empresa do campo: da conversa (os ids da janela), de um `id` que a consulta ao ERP
 * encontrou, ou da lista — inclusive a recém-criada pela ficha.
 */
type Escolha =
  | { origem: 'conversa' }
  | { origem: 'id'; id: string }
  | { origem: 'lista'; empresa: EmpresaEscolhida | null }

/** O vínculo da Pessoa solta depois de o Negócio nascer. */
type Vinculo =
  | { estado: 'nenhum' }
  | { estado: 'vinculando' }
  | { estado: 'feito' }
  | { estado: 'de-outra' }
  | { estado: 'fora-do-alcance' }
  | { estado: 'falhou'; mensagem: string; pessoaId: string; empresaId: string; codigo: number }

export interface PropriedadesDoFormularioDeNegocio {
  /** O Funil em que o Negócio vai nascer. `undefined` enquanto o catálogo não chegou. */
  funilId?: string
  /** Chamado quando a pessoa troca de Funil — quem guarda a escolha é a tela (na URL). */
  aoTrocarDeFunil: (funilId: string) => void
  /** A Empresa da conversa de WhatsApp (13.1): a janela abre com ela escolhida. */
  empresaInicialId?: string
  /** A Pessoa da conversa: relida ao abrir; se solta, é vinculada à Empresa do Negócio criado. */
  pessoaSoltaId?: string
  /** O nome exibido do contato (veio no *state* do histórico, nunca na URL). */
  nomeDoContato?: string
  /**
   * 13.14b: o vínculo no ar (do envio ao fim do vínculo), solto (a falha, com o código do Negócio)
   * ou livre — é o que a janela usa para travar o fechar e para perguntar antes de fechar.
   */
  aoMudarOVinculo?: (estado: EstadoDoVinculoNaJanela, codigo?: number) => void
  /**
   * 13.20b2: abre o Negócio sem Empresa que a Pessoa da conversa já tem — quem monta a janela a
   * fecha e abre o modal do Negócio. Sem ela, o aviso aparece sem o botão.
   */
  aoAbrirONegocio?: (negocioId: string) => void
}

export function FormularioDeNegocio({
  funilId,
  aoTrocarDeFunil,
  empresaInicialId,
  pessoaSoltaId,
  nomeDoContato,
  aoMudarOVinculo,
  aoAbrirONegocio,
}: PropriedadesDoFormularioDeNegocio) {
  const { data: sessao } = useSessao()
  const podeCriarEmpresa = sessao !== undefined && 'criar_empresa' in sessao.permissoes
  const ehCelular = useEhCelular()

  const catalogo = useFunis()
  const equipe = useResponsaveis()
  const criacaoDoNegocio = useCriarNegocio()
  const vinculacao = useVincularPessoaAEmpresa()

  const [rascunho, setRascunho] = useState<Rascunho>(RASCUNHO_VAZIO)
  const [erro, setErro] = useState<string | null>(null)
  const [erroDaEmpresa, setErroDaEmpresa] = useState<string | null>(null)
  /*
   * A ficha do modal terminou com uma linha de Pessoa que **adotou** a Pessoa solta do WhatsApp
   * (13.4): o contato entrou na Empresa criada, e a janela diz isso junto do campo de Empresa.
   * Trocar a Empresa escolhida apaga o aviso — ele fala daquela ficha.
   */
  const [contatoAdotado, setContatoAdotado] = useState(false)
  const [codigoCriado, setCodigoCriado] = useState<number | null>(null)
  const [escolha, setEscolha] = useState<Escolha>({ origem: 'conversa' })
  /* A chave do campo: trocá-la depois de criar devolve o campo ao branco (texto e lista). */
  const [chaveDoCampo, setChaveDoCampo] = useState(0)
  /* O texto que a ficha do modal traz, ou `null` com o modal fechado. */
  const [fichaAberta, setFichaAberta] = useState<{ texto: string } | null>(null)
  /* A Pessoa da conversa só é vinculada **uma** vez: o segundo Negócio da janela não a toca. */
  const [pessoaConsumida, setPessoaConsumida] = useState(false)
  const [vinculo, setVinculo] = useState<Vinculo>({ estado: 'nenhum' })
  const campoDaEmpresa = useRef<HTMLInputElement>(null)

  /*
   * A releitura da Pessoa ao abrir: chave **própria**, e não a do lote da tela de Conversa — aquela
   * guarda o nome por cinco minutos, e aqui a pergunta é "ela ainda está solta agora?".
   */
  const releitura = useQuery({
    queryKey: ['cadastro', 'pessoas', 'releitura', pessoaSoltaId ?? ''],
    queryFn: () => lerPessoasPorIds([pessoaSoltaId as string]),
    enabled: pessoaSoltaId !== undefined,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
  const pessoaLida: Pessoa | undefined = releitura.data?.[0]
  /* 13.20b2: os Negócios em andamento do contato da conversa (`GET /negocios?pessoa_id=`, sob a
     policy de quem age): o sem Empresa e o ex-N0 (code review, A1). Com um, a janela não cria
     outro (FR-3); enquanto a pergunta está no ar, o envio espera; e a pergunta que falhou é "não
     sei" (A2): a janela bloqueia com a mensagem do serviço. */
  const doContato = useNegociosDaPessoa(pessoaSoltaId)
  const negocioSemEmpresaDoContato = doContato.data?.[0]
  const perguntandoPeloContato = pessoaSoltaId !== undefined && doContato.isPending
  const recorteFalhou = pessoaSoltaId !== undefined && doContato.isError
  const pessoaIlegivel =
    pessoaSoltaId !== undefined && (releitura.isError || (releitura.isSuccess && !pessoaLida))
  const pessoaSolta = pessoaLida !== undefined && ehSolta(pessoaLida)
  /* `empresa` vale sobre a Pessoa; a Pessoa com **exatamente uma** Empresa em que quem cria pode
     criar Negócio (as editáveis: `negocio_insercao` do `negocios`; code review da 13.19c2, C) vem
     com ela escolhida — com duas ou mais, ou só vista pela PA-3, quem cria escolhe, e o campo abre
     vazio. */
  const [unicaDaPessoa, ...outrasDaPessoa] =
    pessoaLida === undefined ? [] : empresaIdsEditaveisDe(pessoaLida)
  const empresaDaConversaId =
    empresaInicialId ?? (outrasDaPessoa.length === 0 ? unicaDaPessoa : undefined)

  const idParaLer =
    escolha.origem === 'conversa'
      ? empresaDaConversaId
      : escolha.origem === 'id'
        ? escolha.id
        : undefined
  const leituraDaEmpresa = useEmpresa(idParaLer)
  const escolhida: EmpresaEscolhida | null =
    escolha.origem === 'lista'
      ? escolha.empresa
      : leituraDaEmpresa.data
        ? { id: leituraDaEmpresa.data.id, nome: nomeExibidoDaEmpresa(leituraDaEmpresa.data) }
        : null
  const lendoAEmpresa = idParaLer !== undefined && leituraDaEmpresa.isPending
  /* A Empresa da conversa — a do link ou a da Pessoa relida — que não pôde ser lida: o campo fica
     vazio, e o aviso diz por quê. */
  const empresaDaConversaIlegivel =
    escolha.origem === 'conversa' && empresaDaConversaId !== undefined && leituraDaEmpresa.isError
  /* A ficha que o ERP achou no modal e cuja leitura falhou: sem o aviso, o campo ficaria em
     branco calado depois de o modal fechar dizendo que achou. */
  const empresaEncontradaIlegivel = escolha.origem === 'id' && leituraDaEmpresa.isError

  const funis: Funil[] = catalogo.data ?? []
  const funilAtual = funis.find((funil) => funil.id === funilId)
  const etapas = funilAtual?.etapas ?? []
  // Quem saiu da equipe não recebe Negócio novo: um Responsável desativado não é uma escolha.
  // Nomear o desligado (FR-65) é problema das telas que mostram Negócio **existente** — aqui
  // não há atribuição antiga para preservar.
  const escolhiveis = (equipe.data ?? []).filter((pessoa) => pessoa.ativo)

  // O catálogo é pré-condição: sem Funil e sem Etapa não há onde o Negócio nascer, e a recusa
  // local `FALTA_ETAPA` ("este Funil ainda não tem Etapas") seria **falsa** enquanto o `GET`
  // viaja ou depois de ele falhar. Melhor não deixar enviar e dizer o que de fato aconteceu.
  const catalogoIndisponivel = catalogo.isLoading || catalogo.isError

  // O padrão dos dois campos que têm um: a primeira Etapa do Funil e a própria pessoa como
  // Responsável (distribuição é sempre humana, e quem cadastra é o palpite honesto). Eles são
  // derivados, e não copiados para o estado por um efeito: um efeito que copiasse descartaria a
  // escolha da pessoa no instante em que o catálogo terminasse de chegar.
  //
  // A Etapa escolhida só vale se ela **pertencer** ao Funil aberto. Sem essa condição, trocar de
  // Funil depois de escolher deixaria `rascunho.etapa_id` apontando para a Etapa do Funil
  // anterior: o seletor apareceria em branco (nenhuma `<option>` casa) e o envio criaria o
  // Negócio no Funil errado — o oposto do que o FR-4 promete.
  const etapaEscolhida = etapas.some((etapa) => etapa.id === rascunho.etapa_id)
    ? rascunho.etapa_id
    : (etapas[0]?.id ?? '')
  const responsavelEscolhido =
    rascunho.responsavel_id !== '' ? rascunho.responsavel_id : (sessao?.usuario_id ?? '')
  // Enquanto a equipe não chegou, o valor que o `select` já carrega não tem `<option>` que o
  // case: sem uma linha para ele, o controle cai calado na primeira pessoa da lista e o envio
  // sai no nome de outra pessoa. O rótulo é o nome de quem entrou, que a sessão já traz — um
  // uuid cru na tela não diz nada a ninguém.
  const responsavelSemRotulo =
    responsavelEscolhido !== '' &&
    !escolhiveis.some((pessoa) => pessoa.id === responsavelEscolhido)

  const enviando = criacaoDoNegocio.isPending || vinculo.estado === 'vinculando'
  /* 13.14b: o que a janela precisa saber — o envio e o vínculo no ar, ou o contato solto. */
  const estadoNaJanela: EstadoDoVinculoNaJanela = enviando
    ? 'no-ar'
    : vinculo.estado === 'falhou'
      ? 'pendente'
      : 'livre'
  const codigoSolto = vinculo.estado === 'falhou' ? vinculo.codigo : undefined
  useEffect(() => {
    aoMudarOVinculo?.(estadoNaJanela, codigoSolto)
  }, [aoMudarOVinculo, estadoNaJanela, codigoSolto])

  /**
   * Apaga a confirmação e a recusa anteriores.
   *
   * Qualquer alteração as invalida: os avisos falam do que está no servidor agora, e não do que
   * estava antes de a pessoa mexer de novo. É função própria porque a troca de Funil não passa por
   * `trocar` (ela é do roteador, e não do rascunho) e precisa da mesma limpeza: o código de um
   * Negócio criado no Funil anterior continuando na tela sob outro Funil é a leitura errada mais
   * fácil desta tela. A falha do vínculo **não** é apagada aqui: ela tem um "Tentar de novo" que
   * vale até alguém tentar.
   */
  function limparAvisos() {
    setCodigoCriado(null)
    setErro(null)
    if (
      vinculo.estado === 'feito' ||
      vinculo.estado === 'de-outra' ||
      vinculo.estado === 'fora-do-alcance'
    ) {
      setVinculo({ estado: 'nenhum' })
    }
  }

  function trocar<Chave extends keyof Rascunho>(campo: Chave, valor: Rascunho[Chave]) {
    limparAvisos()
    setRascunho((atual) => ({ ...atual, [campo]: valor }))
  }

  function escolherEmpresa(empresa: EmpresaEscolhida | null) {
    limparAvisos()
    setErroDaEmpresa(null)
    setContatoAdotado(false)
    setEscolha({ origem: 'lista', empresa })
  }

  /**
   * O vínculo da Pessoa solta, **depois** do Negócio: relê no lote e só vincula a que continua
   * solta, com o `empresa_id` do Negócio criado. A Pessoa que já está nesta Empresa (entre as
   * visíveis, 13.19c2) é sucesso; a que está só noutras (ou sumiu do lote) não é tocada — desde a
   * 13.19b2 a rota a somaria à Empresa do Negócio, e com `exigir_solta` é `409`.
   */
  async function vincular(pessoaId: string, empresaId: string, codigo: number) {
    setVinculo({ estado: 'vinculando' })
    try {
      const [pessoa] = await lerPessoasPorIds([pessoaId])
      if (pessoa === undefined) {
        //: 13.14b: sumiu do lote — não é "de outra Empresa", é não estar mais ao alcance.
        setVinculo({ estado: 'fora-do-alcance' })
        return
      }
      if (!ehSolta(pessoa) && !estaNaEmpresa(pessoa, empresaId)) {
        setVinculo({ estado: 'de-outra' })
        return
      }
      if (ehSolta(pessoa)) {
        /* `exigirSolta` (13.3, D-6): entre a releitura e o `POST`, outra aba pode ter vinculado o
           contato; o serviço recusa com `409` em vez de movê-lo. */
        await vinculacao.mutateAsync({ pessoaId, empresaId, exigirSolta: true })
      }
      setVinculo({ estado: 'feito' })
    } catch (falha) {
      if (falha instanceof ErroDaApi && falha.status === STATUS_DE_CONFLITO) {
        // O contato já é de outra Empresa: não há o que tentar de novo.
        setVinculo({ estado: 'de-outra' })
        return
      }
      if (falha instanceof ErroDaApi && falha.status === STATUS_DE_INEXISTENTE) {
        setVinculo({ estado: 'fora-do-alcance' })
        return
      }
      setVinculo({ estado: 'falhou', mensagem: mensagemDe(falha), pessoaId, empresaId, codigo })
    }
  }

  async function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()

    /* 13.20b2: a Pessoa já tem Negócio sem Empresa em andamento (o aviso está na tela), ou a
       pergunta ainda está no ar — nenhum Negócio novo sai. */
    if (negocioSemEmpresaDoContato !== undefined || perguntandoPeloContato || recorteFalhou) {
      return
    }

    // O catálogo é pré-condição, e a guarda vem antes da recusa local: sem Funil carregado a
    // lista de Etapas está vazia por falta de resposta, e `FALTA_ETAPA` diria "este Funil ainda
    // não tem Etapas" — uma frase falsa que manda a pessoa para a Administração à toa.
    if (catalogoIndisponivel) {
      setErro(catalogo.isError ? FUNIS_INDISPONIVEIS : CARREGANDO_OS_FUNIS)
      return
    }
    // Catálogo vazio e Funil sem Etapa são recusas diferentes, e a ordem entre elas importa:
    // `FALTA_ETAPA` fala de "este Funil", e com a lista vazia não há Funil de que falar.
    if (funis.length === 0) {
      setErro(NAO_HA_FUNIL)
      return
    }

    const completo: Rascunho = {
      ...rascunho,
      etapa_id: etapaEscolhida,
      responsavel_id: responsavelEscolhido,
    }
    const recusa = recusaLocal(completo, escolhida?.id ?? null)
    if (recusa === FALTA_EMPRESA && lendoAEmpresa) {
      // A Empresa da conversa (ou a que o ERP achou) ainda está a caminho: "escolha uma Empresa"
      // seria falso — ela já foi escolhida, só não chegou.
      setErroDaEmpresa(AINDA_CARREGANDO_A_EMPRESA)
      return
    }
    if (recusa === FALTA_EMPRESA) {
      // No campo, e não no rodapé: é ali que a pessoa precisa agir.
      setErroDaEmpresa(recusa)
      campoDaEmpresa.current?.focus()
      return
    }
    if (recusa !== null) {
      setErro(recusa)
      return
    }
    if (escolhida === null) {
      return
    }

    setErro(null)
    setCodigoCriado(null)
    /* Decidido **antes** do envio: a Pessoa da conversa vai uma vez só, e só se pode estar solta.
       13.14b (DW-383): só não vai quando a releitura **disse** que ela não está solta (ou que ela
       não é visível); a releitura em erro (ou ainda em voo) não dispensa o vínculo — `vincular`
       relê no lote. */
    const sabidamenteNaoSolta =
      releitura.isSuccess && (pessoaLida === undefined || !ehSolta(pessoaLida))
    const pessoaParaVincular =
      pessoaSoltaId !== undefined && !pessoaConsumida && !sabidamenteNaoSolta
        ? pessoaSoltaId
        : null

    let negocio
    try {
      negocio = await criacaoDoNegocio.mutateAsync({
        empresa_id: escolhida.id,
        etapa_id: completo.etapa_id,
        responsavel_id: completo.responsavel_id,
        sdr_id: ouNulo(completo.sdr_id),
      })
    } catch (falha) {
      // A recusa do serviço vai **verbatim**; a escolha da Empresa permanece.
      setErro(mensagemDe(falha))
      return
    }

    setCodigoCriado(negocio.codigo)
    setRascunho(RASCUNHO_VAZIO)
    setEscolha({ origem: 'lista', empresa: null })
    // O aviso da adoção falava da Empresa que o campo tinha; o campo volta em branco.
    setContatoAdotado(false)
    setChaveDoCampo((chave) => chave + 1)
    if (pessoaSoltaId !== undefined) {
      setPessoaConsumida(true)
    }
    if (pessoaParaVincular !== null && negocio.empresa_id !== null) {
      await vincular(pessoaParaVincular, negocio.empresa_id, negocio.codigo)
    } else if (vinculo.estado !== 'falhou') {
      // A falha do vínculo do Negócio anterior fica, com o "Tentar de novo": o segundo Negócio
      // não a resolve, e apagá-la deixaria o contato solto sem ninguém saber.
      setVinculo({ estado: 'nenhum' })
    }
  }

  /** "Criar nova empresa": o texto digitado, senão o nome do contato solto que tenha letra. */
  function abrirAFicha(texto: string) {
    limparAvisos()
    setErroDaEmpresa(null)
    const doContato =
      pessoaSolta && !pessoaConsumida && nomeDoContato !== undefined && temLetra(nomeDoContato)
        ? nomeDoContato.trim()
        : ''
    // 13.14a (DW-420): o texto vai ao Nome fantasia da ficha (ou ao CNPJ/CPF, quando é só ele).
    setFichaAberta({ texto: texto !== '' ? texto : doContato })
  }

  function fecharAFicha() {
    setFichaAberta(null)
    campoDaEmpresa.current?.focus()
  }

  const telefoneDoContato =
    pessoaSolta && !pessoaConsumida && pessoaLida?.telefone
      ? telefoneDaConversaNaFicha(pessoaLida.telefone)
      : undefined

  return (
    <section className="negocio" aria-labelledby="negocio-titulo">
      <div className="negocio__cartao">
        <div className="negocio__cabecalho">
          <h2 className="negocio__titulo" id="negocio-titulo">
            {TITULO}
          </h2>
          <span className="negocio__ajuda">{AJUDA}</span>
        </div>

        <form className="negocio__corpo" onSubmit={(evento) => void enviar(evento)} noValidate>
          {catalogo.isError ? (
            <p className="negocio__aviso" role="alert">
              {FUNIS_INDISPONIVEIS}
            </p>
          ) : catalogo.isLoading ? (
            <p className="negocio__aviso" role="status">
              {CARREGANDO_OS_FUNIS}
            </p>
          ) : null}
          {equipe.isError ? (
            <p className="negocio__aviso" role="alert">
              {EQUIPE_INDISPONIVEL}
            </p>
          ) : null}
          {empresaDaConversaIlegivel ? (
            <p className="negocio__aviso" role="alert">
              {EMPRESA_DA_CONVERSA_ILEGIVEL}
            </p>
          ) : null}
          {empresaEncontradaIlegivel ? (
            <p className="negocio__aviso" role="alert">
              {EMPRESA_ENCONTRADA_ILEGIVEL}
            </p>
          ) : null}
          {pessoaIlegivel && !pessoaConsumida ? (
            <p className="negocio__aviso" role="alert">
              {CONTATO_DA_CONVERSA_ILEGIVEL}
            </p>
          ) : null}
          {recorteFalhou ? (
            <div className="negocio__ja-tem" id={ID_DO_NEGOCIO_SEM_EMPRESA}>
              <p className="negocio__erro" role="alert">
                {mensagemDe(doContato.error)}
              </p>
              <button
                type="button"
                className="negocio__botao negocio__botao--discreto"
                onClick={() => {
                  void doContato.refetch()
                }}
              >
                {TENTAR_O_RECORTE_DE_NOVO}
              </button>
            </div>
          ) : null}
          {negocioSemEmpresaDoContato === undefined ? null : (
            <div className="negocio__ja-tem" id={ID_DO_NEGOCIO_SEM_EMPRESA}>
              <p className="negocio__ja-tem-texto" role="status">
                {negocioDoContatoEmAndamento(
                  negocioSemEmpresaDoContato.empresa_id === null,
                  negocioSemEmpresaDoContato.responsavel_id === sessao?.usuario_id
                    ? null
                    : ((equipe.data ?? []).find(
                        (pessoa) => pessoa.id === negocioSemEmpresaDoContato.responsavel_id,
                      )?.nome ?? null),
                )}
              </p>
              {aoAbrirONegocio === undefined ? null : (
                <button
                  type="button"
                  className="negocio__botao"
                  onClick={() => {
                    aoAbrirONegocio(negocioSemEmpresaDoContato.id)
                  }}
                >
                  {ABRIR_O_NEGOCIO}
                </button>
              )}
            </div>
          )}

          {/* Uma coluna no celular, duas no computador — decisão de árvore, e não de `@media`.
              Todos os campos existem nas duas larguras (NFR-1). */}
          <div className="negocio__grade" data-colunas={ehCelular ? 'uma' : 'duas'}>
            <Seletor
              id="negocio-funil"
              rotulo="Funil"
              valor={funilId ?? ''}
              aoTrocar={(valor) => {
                limparAvisos()
                aoTrocarDeFunil(valor)
              }}
            >
              {funis.map((funil) => (
                <option key={funil.id} value={funil.id}>
                  {funil.nome}
                </option>
              ))}
            </Seletor>

            <Seletor
              id="negocio-etapa"
              rotulo="Etapa"
              valor={etapaEscolhida}
              aoTrocar={(valor) => {
                trocar('etapa_id', valor)
              }}
            >
              {etapas.map((etapa) => (
                <option key={etapa.id} value={etapa.id}>
                  {etapa.nome}
                </option>
              ))}
            </Seletor>

            <CampoDeEmpresa
              key={chaveDoCampo}
              id="negocio-empresa"
              escolhida={escolhida}
              aoEscolher={escolherEmpresa}
              //: 13.15: "Criar nova empresa" só com `criar_empresa` (o padrão do "+ Pessoa" e do
              //: Grupo) — conveniência: quem recusa é o serviço.
              {...(podeCriarEmpresa ? { aoCriarNova: abrirAFicha } : {})}
              obrigatorio
              erro={erroDaEmpresa}
              placeholder={lendoAEmpresa ? CARREGANDO_A_EMPRESA : undefined}
              refDoCampo={campoDaEmpresa}
              daConversa={escolha.origem === 'conversa'}
            />
            {contatoAdotado ? (
              <p className="negocio__nota" role="status">
                {CONTATO_DO_WHATSAPP_VINCULADO}
              </p>
            ) : null}

            <Seletor
              id="negocio-responsavel"
              rotulo="Responsável"
              valor={responsavelEscolhido}
              aoTrocar={(valor) => {
                trocar('responsavel_id', valor)
              }}
            >
              {/* O valor que o `select` já carrega e que a lista ainda não conhece — a equipe
                  não chegou, ou falhou. Sem esta linha o controle cai calado na primeira pessoa
                  e o envio sai no nome de outra. */}
              {responsavelSemRotulo ? (
                <option value={responsavelEscolhido}>{sessao?.nome ?? responsavelEscolhido}</option>
              ) : null}
              {escolhiveis.map((pessoa) => (
                <option key={pessoa.id} value={pessoa.id}>
                  {pessoa.nome}
                </option>
              ))}
            </Seletor>

            <Seletor
              id="negocio-sdr"
              rotulo="SDR"
              valor={rascunho.sdr_id}
              opcional
              aoTrocar={(valor) => {
                trocar('sdr_id', valor)
              }}
            >
              {escolhiveis.map((pessoa) => (
                <option key={pessoa.id} value={pessoa.id}>
                  {pessoa.nome}
                </option>
              ))}
            </Seletor>
          </div>

          {erro === null ? null : (
            <p className="negocio__erro" role="alert">
              {erro}
            </p>
          )}

          {/* `role="status"` e não `role="alert"`: deu certo, e o leitor de tela não precisa
              interromper o que a pessoa estiver fazendo para dizer isso. */}
          {/* 13.14b (DW-383): com o contato solto, o Negócio não aparece como "pronto" — o
              alerta abaixo diz o código e o que falta. */}
          {codigoCriado === null || vinculo.estado === 'falhou' ? null : (
            <p className="negocio__sucesso" role="status">
              {mensagemDeSucesso(codigoCriado)}
            </p>
          )}

          {vinculo.estado === 'vinculando' ? (
            <p className="negocio__nota" role="status">
              {CONTATO_SENDO_LIGADO}
            </p>
          ) : null}

          {vinculo.estado === 'feito' ? (
            <p className="negocio__nota" role="status">
              {CONTATO_VINCULADO}
            </p>
          ) : vinculo.estado === 'de-outra' ? (
            <p className="negocio__nota" role="status">
              {CONTATO_DE_OUTRA_EMPRESA}
            </p>
          ) : vinculo.estado === 'fora-do-alcance' ? (
            <p className="negocio__nota" role="status">
              {CONTATO_FORA_DO_ALCANCE}
            </p>
          ) : vinculo.estado === 'falhou' ? (
            <div className="negocio__vinculo-falhou" id={ID_DO_CONTATO_SOLTO}>
              <p className="negocio__erro" role="alert">
                {contatoSolto(vinculo.codigo)}
              </p>
              {/* A recusa do serviço, verbatim. */}
              <p className="negocio__erro">{vinculo.mensagem}</p>
              <button
                type="button"
                className="negocio__botao negocio__botao--discreto"
                data-tentar-o-vinculo=""
                onClick={() => {
                  void vincular(vinculo.pessoaId, vinculo.empresaId, vinculo.codigo)
                }}
              >
                {TENTAR_O_VINCULO_DE_NOVO}
              </button>
            </div>
          ) : null}

          <div className="negocio__acoes">
            <button
              className="negocio__botao"
              type="submit"
              disabled={
                enviando ||
                catalogoIndisponivel ||
                vinculo.estado === 'falhou' ||
                negocioSemEmpresaDoContato !== undefined ||
                perguntandoPeloContato ||
                recorteFalhou
              }
              aria-describedby={
                vinculo.estado === 'falhou'
                  ? ID_DO_CONTATO_SOLTO
                  : negocioSemEmpresaDoContato !== undefined || recorteFalhou
                    ? ID_DO_NEGOCIO_SEM_EMPRESA
                    : undefined
              }
            >
              {enviando ? CRIANDO : CRIAR}
            </button>
          </div>
        </form>
      </div>

      {/* Irmão do `<form>`, e não filho: o `submit` da ficha não pode chegar ao do Negócio. */}
      {fichaAberta === null ? null : (
        <ModalDaNovaEmpresa
          textoInicial={fichaAberta.texto}
          telefone={telefoneDoContato}
          responsavelInicial={responsavelEscolhido}
          aoCancelar={fecharAFicha}
          aoEscolher={(empresa, desfecho) => {
            escolherEmpresa(empresa)
            setContatoAdotado(desfecho.contatoAdotado)
            fecharAFicha()
          }}
          aoEncontrar={(empresaId) => {
            // O CNPJ que o ERP já conhece: **aquela** Empresa fica escolhida.
            limparAvisos()
            setErroDaEmpresa(null)
            setContatoAdotado(false)
            setEscolha({ origem: 'id', id: empresaId })
            fecharAFicha()
          }}
        />
      )}
    </section>
  )
}
